import { createContext, forwardRef, useContext, useId } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  CollapseContentProps,
  CollapseIndicator,
  CollapseProps,
  CollapseTitleProps,
} from './types';

const indicatorClasses: Record<CollapseIndicator, string> = {
  none: '',
  arrow: 'collapse-arrow',
  plus: 'collapse-plus',
};

type CollapseContextProps = {
  open: boolean;
  onToggle: (open: boolean) => void;
  contentId: string;
};

const CollapseContext = createContext<CollapseContextProps>({
  open: false,
  onToggle: () => {},
  contentId: '',
});

const CollapseBase = forwardRef<HTMLDivElement, CollapseProps>(
  (
    { className, open, onToggle, indicator = 'none', children, ...props },
    ref,
  ) => {
    const contentId = useId();

    const classes = twMerge(
      'collapse',
      indicatorClasses[indicator],
      open ? 'collapse-open' : 'collapse-close',
      className,
    );

    return (
      <CollapseContext.Provider value={{ open, onToggle, contentId }}>
        <div ref={ref} className={classes} {...props}>
          {children}
        </div>
      </CollapseContext.Provider>
    );
  },
);

const Title = forwardRef<HTMLButtonElement, CollapseTitleProps>(
  ({ className, onClick, children, ...props }, ref) => {
    const { open, onToggle, contentId } = useContext(CollapseContext);

    return (
      <button
        ref={ref}
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        className={twMerge(
          'collapse-title flex w-full cursor-pointer items-center text-left',
          className,
        )}
        onClick={event => {
          onToggle(!open);
          onClick?.(event);
        }}
        {...props}
      >
        {children}
      </button>
    );
  },
);

const Content = forwardRef<HTMLDivElement, CollapseContentProps>(
  ({ className, children, ...props }, ref) => {
    const { contentId } = useContext(CollapseContext);

    return (
      <div
        ref={ref}
        id={contentId}
        className={twMerge('collapse-content', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

CollapseBase.displayName = 'Collapse';
Title.displayName = 'Collapse.Title';
Content.displayName = 'Collapse.Content';

export const Collapse = Object.assign(CollapseBase, {
  Title,
  Content,
});
